import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { toFixed } from "@shared/utils/numbers";
import { formatAmount } from "features/portfolio/NewLook/format.ts";
import { DISPLAY_CURRENCIES } from "common/constants";

/** A true minus sign, so signed figures line up in tabular columns. */
export const MINUS = "−";

/**
 * `value`, or 0 when it reads as zero at `digits` decimals, so "0.00%" takes
 * no sign and no direction colour.
 */
export const shownPercent = (value: number, digits = 2) =>
  Number(Math.abs(value).toFixed(digits)) ? value : 0;

export const formatSignedPercent = (value: number, digits = 2) => {
  const shown = shownPercent(value, digits);
  return `${shown > 0 ? "+" : shown < 0 ? MINUS : ""}${Math.abs(value).toFixed(
    digits
  )}%`;
};

/**
 * Display-currency figures written the dashboard's way: "$2,000",
 * "−$37.03", "+$51.55" (or ₿ when the display currency is BTC).
 */
const useMoneyFormat = () => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const symbol = isBtc ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  // `value`, or 0 when it reads as zero at the display precision ("$0"), so
  // it takes no sign and no direction colour.
  const shown = useCallback(
    (value: number) => (toFixed(Math.abs(value), precision) ? value : 0),
    [precision]
  );
  const money = useCallback(
    (value: number) =>
      `${shown(value) < 0 ? MINUS : ""}${symbol}${formatAmount(
        Math.abs(value),
        precision
      )}`,
    [symbol, precision, shown]
  );
  const signedMoney = useCallback(
    (value: number) => `${shown(value) > 0 ? "+" : ""}${money(value)}`,
    [money, shown]
  );

  return useMemo(
    () => ({ isBtc, symbol, precision, shown, money, signedMoney }),
    [isBtc, symbol, precision, shown, money, signedMoney]
  );
};

export default useMoneyFormat;
